import { useEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AppLayout from './components/AppLayout';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import EmailLogs from './pages/EmailLogs';
import Emails from './pages/Emails';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Ports from './pages/Ports';
import SendEmail from './pages/SendEmail';
import ScheduledJobs from './pages/ScheduledJobs';
import Vessels from './pages/Vessels';
import Health from './pages/Health';
import Help from './pages/Help';
import { useTheme } from './context/ThemeContext';
import { consumeSessionStatus, logoutUser } from './services/authService';
import {
  SESSION_EXPIRED_FLAG_KEY,
  SESSION_EXPIRED_MESSAGE,
  setUnauthorizedHandler,
} from './services/api';

const SESSION_EXPIRED_TOAST_ID = 'session-expired';
const LOGIN_VIEW_PARAM = 'view';
const LOGIN_VIEW_VALUE = 'login';

function showSessionExpiredToast() {
  toast.error(SESSION_EXPIRED_MESSAGE, { toastId: SESSION_EXPIRED_TOAST_ID });
}

function isLoginViewFromUrl() {
  return new URLSearchParams(window.location.search).get(LOGIN_VIEW_PARAM) === LOGIN_VIEW_VALUE;
}

function getLandingUrl() {
  return `${window.location.pathname}${window.location.hash}`;
}

function getLoginUrl() {
  const params = new URLSearchParams(window.location.search);
  params.set(LOGIN_VIEW_PARAM, LOGIN_VIEW_VALUE);
  const query = params.toString();
  return `${window.location.pathname}?${query}${window.location.hash}`;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => consumeSessionStatus() === 'valid'
  );
  const [showLogin, setShowLogin] = useState(() => !isAuthenticated && isLoginViewFromUrl());
  const [activeItem, setActiveItem] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme } = useTheme();
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  // Case 1: user returns / refreshes after 24h — token already expired on load.
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG_KEY) !== '1') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG_KEY);
      showSessionExpiredToast();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  // Keep Landing/Login in sync with browser history (back/forward).
  useEffect(() => {
    const syncAuthViewFromUrl = () => {
      if (isAuthenticatedRef.current) {
        return;
      }
      setShowLogin(isLoginViewFromUrl());
    };

    window.addEventListener('popstate', syncAuthViewFromUrl);
    return () => window.removeEventListener('popstate', syncAuthViewFromUrl);
  }, []);

  // Case 2: token expires while using the app — next API call hits 401 / client TTL.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      const wasAuthenticated = isAuthenticatedRef.current;
      logoutUser();
      setIsAuthenticated(false);
      setShowLogin(false);
      setActiveItem('home');
      window.history.replaceState({ page: 'landing' }, '', getLandingUrl());

      if (wasAuthenticated) {
        window.setTimeout(() => {
          showSessionExpiredToast();
        }, 0);
      }
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  const handleNavigate = (itemId) => {
    setActiveItem(itemId);
    setSidebarOpen(false);
  };

  const openLogin = () => {
    if (isLoginViewFromUrl()) {
      setShowLogin(true);
      return;
    }

    window.history.pushState({ page: 'login' }, '', getLoginUrl());
    setShowLogin(true);
  };

  const goToLanding = ({ useHistoryBack = false } = {}) => {
    if (useHistoryBack && window.history.state?.page === 'login') {
      window.history.back();
      return;
    }

    window.history.replaceState({ page: 'landing' }, '', getLandingUrl());
    setShowLogin(false);
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowLogin(false);
    setActiveItem('home');
    window.history.replaceState({ page: 'app' }, '', getLandingUrl());
  };

  const handleLogout = () => {
    logoutUser();
    setIsAuthenticated(false);
    setShowLogin(false);
    setActiveItem('home');
    window.history.replaceState({ page: 'landing' }, '', getLandingUrl());
  };

  const renderPage = () => {
    if (activeItem === 'send-email') {
      return <SendEmail />;
    }

    if (activeItem === 'logs') {
      return <EmailLogs />;
    }

    if (activeItem === 'scheduled-jobs') {
      return <ScheduledJobs />;
    }

    if (activeItem === 'emails') {
      return <Emails />;
    }

    if (activeItem === 'ports') {
      return <Ports />;
    }

    if (activeItem === 'vessels') {
      return <Vessels />;
    }

    if (activeItem === 'profile') {
      return <Profile />;
    }

    if (activeItem === 'health') {
      return <Health />;
    }

    if (activeItem === 'help') {
      return <Help />;
    }

    return <Dashboard />;
  };

  return (
    <>
      {isAuthenticated ? (
        <AppLayout
          activeItem={activeItem}
          onNavigate={handleNavigate}
          sidebarOpen={sidebarOpen}
          onOpenSidebar={() => setSidebarOpen(true)}
          onCloseSidebar={() => setSidebarOpen(false)}
          onLogout={handleLogout}
        >
          {renderPage()}
        </AppLayout>
      ) : showLogin ? (
        <Login
          onLoginSuccess={handleLoginSuccess}
          onBack={() => goToLanding({ useHistoryBack: true })}
        />
      ) : (
        <Landing onLoginClick={openLogin} />
      )}
      <ToastContainer
        position="top-right"
        theme={theme}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
    </>
  );
}

export default App;
